import React, { FormEvent, useEffect, useState } from "react";
import api from "../../../api";
import "../../../styles/Login.scss";

interface Range {
    id: number;
    name: string;
    active: boolean;
    display_order: number;
}

interface RangeModalProps {
    range?: Range | null;
    onClose: () => void;
    onSaved: () => void;
}

const RangeModal: React.FC<RangeModalProps> = ({
                                                   range,
                                                   onClose,
                                                   onSaved,
                                               }) => {
    const isEditing = !!range;

    const [name, setName] = useState(range?.name ?? "");
    const [active, setActive] = useState(range?.active ?? true);
    const [displayOrder, setDisplayOrder] = useState(
        range?.display_order ?? 0
    );

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        setName(range?.name ?? "");
        setActive(range?.active ?? true);
        setDisplayOrder(range?.display_order ?? 0);
        setError("");
    }, [range]);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        setError("");

        if (!name.trim()) {
            setError("Range name is required.");
            return;
        }

        try {
            setLoading(true);

            const payload = {
                name: name.trim(),
                active,
                display_order: Number(displayOrder),
            };

            if (isEditing && range) {
                await api.put(
                    `/admin/ranges/${range.id}`,
                    payload
                );
            } else {
                await api.post("/admin/ranges", payload);
            }

            onSaved();
        } catch (err: any) {
            console.error(err);

            if (err.response?.data?.errors) {
                const validationErrors =
                    err.response.data.errors;

                const firstError =
                    Object.values(validationErrors)[0];

                if (Array.isArray(firstError)) {
                    setError(String(firstError[0]));
                } else {
                    setError(String(firstError));
                }
            } else {
                setError(
                    err.response?.data?.message ||
                    "Failed to save range."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-modal-overlay">
            <div className="admin-modal admin-range-modal">
                <div className="admin-modal-header">
                    <h2>
                        {isEditing
                            ? "Edit Range"
                            : "Add Range"}
                    </h2>

                    <button
                        type="button"
                        className="admin-modal-close"
                        onClick={onClose}
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="admin-modal-content"
                >
                    {error && (
                        <div className="form-error">
                            {error}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="range-name">
                            Range Name
                        </label>

                        <input
                            id="range-name"
                            type="text"
                            value={name}
                            onChange={(e) =>
                                setName(e.target.value)
                            }
                            placeholder="e.g. Safari Original"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="range-display-order">
                            Display Order
                        </label>

                        <input
                            id="range-display-order"
                            type="number"
                            min="0"
                            value={displayOrder}
                            onChange={(e) =>
                                setDisplayOrder(
                                    Number(e.target.value)
                                )
                            }
                        />
                    </div>

                    <div className="form-group">
                        <label className="admin-checkbox-label">
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={(e) =>
                                    setActive(
                                        e.target.checked
                                    )
                                }
                            />

                            <span>Active</span>
                        </label>
                    </div>

                    <div className="admin-modal-actions">
                        <button
                            type="button"
                            className="admin-table-button"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="admin-primary-button"
                            disabled={loading}
                        >
                            {loading
                                ? "Saving..."
                                : "Save Changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RangeModal;
